// LE MODE HORS LIGNE côté client (25/07, option B validée par Claude-Alain).
//
// Trois choses, toutes optionnelles et silencieuses en cas d'échec :
//   1. l'enregistrement du service worker (c'est lui qui rend la page ouvrable sans réseau) ;
//   2. un bandeau discret qui DIT l'état des choses, plutôt que de laisser découvrir la perte ;
//   3. le vidage de la file des chronos dès que le réseau revient.
//
// ⚠️ Mise à jour du jeu : le service worker prend la main tout de suite (skipWaiting), mais la page
// déjà chargée continue de faire tourner l'ANCIEN bundle. On ne la recharge donc pas de force au
// milieu d'une course : `reloadIfUpdated()` est appelé depuis l'accueil, seul endroit où un
// rechargement ne coûte rien à personne.
import { ApiError, apiCreateLevel, apiSubmitScore } from './api'
import { dropDraft, readDrafts } from './maps-offline'
import {
  dropScore, enqueueScore, outcomeFor, readQueue, type QueuedScore,
} from './score-queue'
import type { ScoreResponse } from '../shared/api-types'

/** Vrai quand une nouvelle version du jeu a pris la main pendant cette session. */
let updateReady = false
/** Empêche deux vidages simultanés de la file (retour de réseau + démarrage, par exemple). */
let flushing = false
/** Idem pour la publication des cartes créées hors ligne. */
let flushingDrafts = false

/**
 * Enregistre le service worker. Sans lui, rien du mode hors ligne ne fonctionne : la page elle-même
 * ne s'ouvrirait pas. Échec silencieux (navigateur ancien, page servie en http hors localhost) :
 * le jeu marche alors exactement comme avant.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  // ⚠️ La PREMIÈRE installation déclenche aussi `controllerchange` (le service worker prend en
  // charge une page qui ne l'était pas). Sans cette garde, la toute première visite se solderait
  // par un rechargement immédiat sans raison. On ne considère comme MISE À JOUR qu'un changement
  // de contrôleur sur une page qui en avait déjà un.
  const dejaControlee = navigator.serviceWorker.controller !== null
  navigator.serviceWorker.register('/sw.js').catch(() => { /* pas de hors ligne, jeu intact */ })
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (dejaControlee) { updateReady = true; montreBandeauMaj() }
  })
}

/** Recharge la page SI une nouvelle version a pris la main. À appeler depuis l'accueil seulement. */
export function reloadIfUpdated(): void {
  if (!updateReady) return
  updateReady = false
  location.reload()
}

/**
 * LE BANDEAU « nouvelle version ». Il existe parce que `reloadIfUpdated()` n'est appelé QUE
 * depuis l'accueil : un joueur qui ouvre le jeu et va droit dans Campagnes ou au Vestiaire garde
 * l'ancienne version aussi longtemps qu'il ne repasse pas par l'accueil. C'est exactement ce qui
 * est arrivé le 01/08 — deux écrans neufs livrés et déployés, et introuvables à l'écran.
 *
 * Il ne recharge JAMAIS tout seul : c'est un bouton. Un rechargement d'office arracherait une
 * partie en cours, et c'est précisément ce que la règle « seulement depuis l'accueil » évitait.
 */
function montreBandeauMaj(): void {
  if (document.getElementById('maj-prete')) return
  const b = document.createElement('button')
  b.id = 'maj-prete'
  b.type = 'button'
  b.textContent = '⬆ Nouvelle version du jeu — appuie pour la charger'
  b.style.cssText = [
    'position:fixed', 'left:50%', 'top:10px', 'transform:translateX(-50%)', 'z-index:70',
    'padding:9px 16px', 'border-radius:999px', 'border:1px solid #ffd23f88',
    'background:#241f0e', 'color:#ffd23f', 'font-family:ui-monospace,Menlo,monospace',
    'font-size:12px', 'font-weight:700', 'cursor:pointer', 'max-width:92vw',
    'box-shadow:0 8px 30px -8px #000',
  ].join(';')
  b.addEventListener('click', () => location.reload())
  document.body.appendChild(b)
}

/** Vrai si le navigateur se sait hors ligne. (`onLine` ment parfois dans l'autre sens : un « true »
 *  ne garantit pas Internet. On ne s'en sert donc que pour AFFICHER, jamais pour décider.) */
export function isOffline(): boolean {
  return navigator.onLine === false
}

/**
 * Bandeau d'état. Le texte est une promesse tenue par la file : hors ligne, les temps sont gardés.
 * Rien ne s'affiche tant qu'on est en ligne.
 */
function paintBanner(): void {
  const el = document.getElementById('offline-flag')
  if (!el) return
  const n = readQueue(localStorage).length
  const c = readDrafts(localStorage).length
  // On énumère ce qui attend : un joueur qui a créé une carte au chalet doit VOIR qu'elle est
  // toujours là. Un compteur muet laisserait croire que le travail a été perdu.
  const attente = [
    n > 0 ? `${n} temps` : '',
    c > 0 ? `${c} carte${c > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' et ')
  if (isOffline()) {
    el.textContent = attente
      ? `Hors ligne · ${attente} en attente, ça partira au retour du réseau`
      : 'Hors ligne · tes temps et tes cartes partiront au retour du réseau'
    el.hidden = false
  } else if (attente) {
    el.textContent = `${attente} en attente d'envoi…`
    el.hidden = false
  } else {
    el.hidden = true
  }
}

/**
 * Range un temps dans la file. TOUJOURS appelé, même en ligne : c'est ce qui garantit qu'aucun temps
 * ne se perd si le réseau tombe entre la victoire et l'envoi. Le vidage qui suit l'enverra
 * immédiatement dans le cas normal.
 */
export function keepScore(slug: string, timeMs: number, deaths: number, mode: { dash: boolean; torch: boolean }): void {
  enqueueScore(localStorage, { slug, timeMs, deaths, dash: mode.dash, torch: mode.torch, at: Date.now() })
  paintBanner()
}

/**
 * Envoie la file, entrée par entrée, dans l'ordre. S'arrête à la première panne réseau (les entrées
 * restantes attendront le prochain retour) ; jette les entrées définitivement refusées pour qu'une
 * seule carte supprimée ne bloque pas tous les temps suivants.
 * `onFete` reçoit la réponse des envois réussis (défis accomplis, duels remportés) : un record fait
 * au chalet peut donc remporter un duel plusieurs jours plus tard, et le toast tombe au retour.
 */
export async function flushScores(onFete?: (r: ScoreResponse) => void): Promise<void> {
  if (flushing) return
  flushing = true
  try {
    for (const entry of readQueue(localStorage)) {
      let status: number | null = null
      let res: ScoreResponse | null = null
      try {
        res = await apiSubmitScore(entry.slug, entry.timeMs, entry.deaths, entry.dash, entry.torch)
        status = 200
      } catch (e) {
        status = e instanceof ApiError ? e.status : null
      }
      const sort = outcomeFor(status)
      if (sort === 'reseau') break        // hors ligne ou session à renouveler : on garde tout
      dropScore(localStorage, entry)      // envoyé, ou refusé sans espoir : dans les deux cas, dehors
      if (sort === 'envoye' && res) onFete?.(res)
    }
  } finally {
    flushing = false
    paintBanner()
  }
}

/**
 * Publie les cartes créées sans réseau, dans l'ordre. Même discipline que les chronos : on s'arrête
 * à la première panne (le reste attendra le prochain retour), et on JETTE une carte définitivement
 * refusée (400 : grille invalide) pour qu'elle ne bloque pas la publication des suivantes.
 * Renvoie le nombre de cartes réellement publiées, pour l'annoncer à l'auteur.
 */
export async function flushDrafts(): Promise<number> {
  if (flushingDrafts) return 0
  flushingDrafts = true
  let publiees = 0
  try {
    for (const d of readDrafts(localStorage)) {
      let status: number | null = null
      try {
        await apiCreateLevel(d.name, d.ascii, d.world, d.monkSpeed, d.smartMonks, d.decor, d.monkPowers)
        status = 200
      } catch (e) {
        status = e instanceof ApiError ? e.status : null
      }
      const sort = outcomeFor(status)
      if (sort === 'reseau') break
      dropDraft(localStorage, d.localId)
      if (sort === 'envoye') publiees++
    }
  } finally {
    flushingDrafts = false
    paintBanner()
  }
  return publiees
}

/** Nombre de cartes encore en attente de publication. */
export function pendingDraftCount(): number {
  return readDrafts(localStorage).length
}

/**
 * Branche le suivi de connexion : bandeau à jour, et vidage de la file dès que le réseau revient.
 * À appeler une fois au démarrage.
 */
export function watchConnection(onFete?: (r: ScoreResponse) => void): void {
  addEventListener('online', () => { paintBanner(); void flushScores(onFete); void flushDrafts() })
  addEventListener('offline', paintBanner)
  paintBanner()
}

/** Nombre de temps encore en attente (pour l'affichage). */
export function pendingScoreCount(): number {
  return readQueue(localStorage).length
}
